import React, { useState, useEffect } from "react";
import {
  CrudForm,
  Autocomplete,
  crudFormEnums,
  FormApi,
  DatePicker,
  SimpleFormDialog,
  SimpleDialog,
  MountEntity,
  autocompleteHelpers,
  loopback,
} from "elentari";
import Grid from "@material-ui/core/Grid";
import SendIcon from "@material-ui/icons/Send";
import moment from "moment";
import { path } from "ramda";
import { TextField } from "final-form-material-ui";
import { Field } from "react-final-form";
import { secaApi, clienteApi } from "../../services/Api";
import { mountImagesForm } from "./imageLib";
import ImagePicker from "../../components/ImagePicker";
import { getFcmAfterLoad } from "../Fcm/getFcmAfterLoad";

const { toOption } = autocompleteHelpers;
const { loopbackFilters } = loopback;

const loadClientes = async (inputValue) => {
  const response = await clienteApi.getList({
    where: {
      nome: loopbackFilters.regexp(inputValue),
    },
    order: "nome asc",
    limit: 40,
  });
  if (response.ok) {
    return response.data.map(toOption("nome"));
  }
  return [];
};

export default function SecaForm(props) {
  const [statusNotificacao, setStatusNotificacao] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorOnSendMessage, setErrorOnSendMessage] = useState(false);
  const FCMUtils = getFcmAfterLoad();
  const id = props.match.params.childId
    ? props.match.params.childId
    : props.match.params.id;
  const clienteId = props.match.params.childId && props.match.params.id;
  const isNew = id === "new" || id === "new-child";

  const fetchSeca = async (event) => {
    if (isNew) {
      setStatusNotificacao(false);
      return;
    }
    const response = await secaApi.getOne(id);
    if (clienteId) FCMUtils.getClientFcmById(clienteId);

    if (response.ok) setStatusNotificacao(response.data.notificado);
  };

  useEffect(() => {
    fetchSeca();
  }, []);

  const getItemDecorator = async ({ getItem, filter }) => {
    if (!clienteId && props.showClient && !isNew) {
      const result = await MountEntity(props, secaApi.getOne)
        .populate(({ data, id }) => ({
          cliente: clienteApi.getOne(data.clienteId),
        }))
        .clean(["clienteId"])
        .map((data) => ({
          ...data,
          cliente: toOption("nome")(data.cliente),
        }))
        .getItem();
      FCMUtils.getClientFcmById(result.cliente.id);
      return result.id ? result : { data: moment().format() };
    }
    return getItem(filter);
  };

  const submitDecorator = async ({ id, data, handleSubmit }) => {
    const result = await handleSubmit({
      id,
      data: {
        ...data,
        clienteId: clienteId ? Number(clienteId) : data.cliente.id,
        cliente: undefined,
        data: data.data,
        peso: data.peso ? +data.peso : undefined,
        percentual: data.percentual ? +data.percentual : undefined,
        mme: data.mme ? +data.mme : undefined,
        gorduraVisceral: data.gorduraVisceral
          ? +data.gorduraVisceral
          : undefined,
        gordura: data.gordura ? +data.gordura : undefined,
        circunferenciaCintura: data.circunferenciaCintura
          ? +data.circunferenciaCintura
          : undefined,
        imagemFrente: Array.isArray(data.imagemFrente)
          ? undefined
          : data.imagemFrente,
        imagemCostas: Array.isArray(data.imagemCostas)
          ? undefined
          : data.imagemCostas,
        imagemLateral: Array.isArray(data.imagemLateral)
          ? undefined
          : data.imagemLateral,
      },
    });
    FCMUtils.getClientFcmById(clienteId ? Number(clienteId) : data.cliente.id);
    const imagesForm = mountImagesForm(data);
    if (result.ok && imagesForm) {
      await secaApi.uploadImages(
        path(["data", "id"], result) || id,
        imagesForm
      );
    }
    return result;
  };

  const enviarNotificacao = async (title, body) => {
    const response = await secaApi.notify(id, { title, body });

    if (!response.ok) setErrorOnSendMessage(true);
  };

  const customActions = [
    {
      label: statusNotificacao ? "Notificar novamente" : "Notificar",
      icon: <SendIcon style={{ marginLeft: 10 }} />,
      onClick: () => setDialogOpen(true),
      pristine: crudFormEnums.PRISTINE_BEHAVIOR.DISABLED_WHEN_DIRTY,
    },
  ];

  const validate = (values) => ({
    data: values.data ? undefined : "Obrigatório",
    cliente: !props.showClient || values.cliente ? undefined : "Obrigatório",
    peso: values.peso ? undefined : "Obrigatório",
    mme: values.mme ? undefined : "Obrigatório",
    gordura: values.gordura ? undefined : "Obrigatório",
    percentual: values.gordura ? undefined : "Obrigatório",
    gorduraVisceral: values.gorduraVisceral ? undefined : "Obrigatório",
    circunferenciaCintura: values.circunferenciaCintura
      ? undefined
      : "Obrigatório",
  });

  return (
    <div>
      <FormApi
        {...props}
        getItemDecorator={getItemDecorator}
        initialData={{ data: moment().format() }}
        submitDecorator={submitDecorator}
        api={secaApi}
      >
        {({ handleSubmit, getItem }) => (
          <CrudForm
            withPaper={props.withPaper}
            validate={validate}
            history={props.history}
            customActions={!isNew ? customActions : []}
            getItem={getItem}
            onSubmit={handleSubmit}
          >
            <Grid container direction="column" spacing={16}>
              <Grid item>
                <Grid container spacing={16}>
                  {props.showClient && (
                    <Grid item sm={9} xs={12}>
                      <Field
                        label="Cliente"
                        loadOptions={loadClientes}
                        component={Autocomplete}
                        name="cliente"
                      />
                    </Grid>
                  )}
                  <Grid item sm={3} xs={12}>
                    <Field
                      fullWidth
                      label="Data"
                      component={DatePicker}
                      name="data"
                    />
                  </Grid>
                  <Grid item sm={4} xs={12}>
                    <Field
                      type="number"
                      fullWidth
                      autoFocus
                      component={TextField}
                      label="Peso"
                      name="peso"
                    />
                  </Grid>
                  <Grid item sm={4}>
                    <Field
                      type="number"
                      fullWidth
                      component={TextField}
                      label="Massa Muscular Esquelética"
                      name="mme"
                    />
                  </Grid>
                  <Grid item sm>
                    <Field
                      type="number"
                      fullWidth
                      component={TextField}
                      label="Gordura"
                      name="gordura"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container spacing={16}>
                  <Grid item sm>
                    <Field
                      type="number"
                      fullWidth
                      component={TextField}
                      label="Percentual de Gordura"
                      name="percentual"
                    />
                  </Grid>
                  <Grid item sm>
                    <Field
                      type="number"
                      fullWidth
                      component={TextField}
                      label="Gordura Visceral"
                      name="gorduraVisceral"
                    />
                  </Grid>
                  <Grid item sm>
                    <Field
                      type="number"
                      fullWidth
                      component={TextField}
                      label="Circunferência Cintura"
                      name="circunferenciaCintura"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container justify="space-around" style={{ marginTop: 18 }}>
                <Grid item xs="auto">
                  <Field
                    fullWidth
                    width={150}
                    height={300}
                    defaultImage={require("../../images/empty-image.png")}
                    component={ImagePicker}
                    label="Frente"
                    name="imagemFrente"
                  />
                </Grid>
                <Grid item xs="auto">
                  <Field
                    fullWidth
                    width={150}
                    height={300}
                    defaultImage={require("../../images/empty-image.png")}
                    component={ImagePicker}
                    label="Lateral"
                    name="imagemLateral"
                  />
                </Grid>
                <Grid item xs="auto">
                  <Field
                    fullWidth
                    width={150}
                    height={300}
                    defaultImage={require("../../images/empty-image.png")}
                    component={ImagePicker}
                    label="Costas"
                    name="imagemCostas"
                  />
                </Grid>
              </Grid>
            </Grid>
          </CrudForm>
        )}
      </FormApi>
      <SimpleFormDialog
        title="Notificar"
        open={dialogOpen}
        buttonLabel="Cancelar"
        primaryAction={(title, body) =>
          enviarNotificacao(title, body).then(fetchSeca)
        }
        primaryActionButtonLabel="Enviar"
        firstFormLabel="Título"
        firstDefaultValue="Avaliação"
        secondFormLabel="Mensagem"
        secondDefaultValue="Sua avaliação está disponível!"
        disabledText={FCMUtils.state}
        handleClose={() => {
          setDialogOpen(false);
          fetchSeca();
        }}
      />
      <SimpleDialog
        title="Atenção"
        message="Ocorreu um erro no envio da mensagem, por favor tente novamente mais tarde."
        open={errorOnSendMessage}
        primaryActionButtonLabel="Ok"
        handleClose={() => setErrorOnSendMessage(null)}
      />
    </div>
  );
}
